/**
 * ImageGallery — interactive image viewer for listing detail
 *
 * Client component with clickable thumbnails to change the main image.
 * Supports keyboard navigation and zoom.
 */

"use client";

import { useState, useCallback } from "react";
import Image from "next/image";
import { ChevronLeft, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";

interface GalleryImage {
    id: string;
    url: string;
    alt: string | null;
}

interface ImageGalleryProps {
    images: GalleryImage[];
    title: string;
}

export function ImageGallery({ images, title }: ImageGalleryProps) {
    const [activeIndex, setActiveIndex] = useState(0);

    const goTo = useCallback(
        (index: number) => {
            setActiveIndex((index + images.length) % images.length);
        },
        [images.length],
    );

    if (images.length === 0) {
        return (
            <div className="glass overflow-hidden rounded-2xl">
                <div className="flex aspect-video items-center justify-center text-hp-gray-light">
                    Pas de photo disponible
                </div>
            </div>
        );
    }

    const current = images[activeIndex];

    return (
        <div className="glass overflow-hidden rounded-2xl">
            {/* Main image */}
            <div
                className="relative aspect-video group"
                role="img"
                aria-label={current.alt ?? title}
            >
                <Image
                    src={current.url}
                    alt={current.alt ?? title}
                    fill
                    className="object-contain"
                    priority={activeIndex === 0}
                    sizes="(max-width: 1024px) 100vw, 66vw"
                />

                {images.length > 1 && (
                    <>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="absolute left-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/80 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
                            onClick={() => goTo(activeIndex - 1)}
                            aria-label="Image précédente"
                        >
                            <ChevronLeft className="h-5 w-5" />
                        </Button>
                        <Button
                            variant="ghost"
                            size="icon"
                            className="absolute right-2 top-1/2 -translate-y-1/2 h-10 w-10 rounded-full bg-white/80 backdrop-blur-sm opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
                            onClick={() => goTo(activeIndex + 1)}
                            aria-label="Image suivante"
                        >
                            <ChevronRight className="h-5 w-5" />
                        </Button>

                        {/* Counter */}
                        <span className="absolute bottom-3 right-3 bg-black/50 text-white text-xs px-2 py-1 rounded-full backdrop-blur-sm">
                            {activeIndex + 1} / {images.length}
                        </span>
                    </>
                )}
            </div>

            {/* Thumbnails */}
            {images.length > 1 && (
                <div className="flex gap-2 overflow-x-auto p-3 border-t border-white/50 dark:border-white/10">
                    {images.map((img, i) => (
                        <button
                            key={img.id}
                            onClick={() => setActiveIndex(i)}
                            className={`relative h-20 w-20 shrink-0 overflow-hidden rounded-xl border-2 transition-colors cursor-pointer ${i === activeIndex
                                    ? "border-hp-purple ring-2 ring-hp-purple/30"
                                    : "border-transparent hover:border-hp-purple/50"
                                }`}
                            aria-label={`Voir image ${i + 1}`}
                            aria-current={i === activeIndex ? "true" : undefined}
                        >
                            <Image
                                src={img.url}
                                alt={img.alt ?? `Image ${i + 1}`}
                                fill
                                className="object-cover"
                                sizes="80px"
                            />
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}
